import { Avatar, Box, IconButton, Typography, useTheme } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { toLongDate } from '@utils/Dated';
import { useDialog, useLocalization } from '@ui-framework/index';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { NOTE_ACTION } from '@constants/notesAttachment';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';
import { deleteNote } from './lib';

const getNoteUserName = ({ lastModifiedBy, createdBy }) => {
  let { firstName = null, lastName = null } = createdBy;
  if (lastModifiedBy?.firstName && lastModifiedBy?.lastName) {
    firstName = lastModifiedBy?.firstName;
    lastName = lastModifiedBy?.lastName;
  }
  return {
    firstName,
    lastName,
  };
};

const getNoteDateTime = ({ createdDateTime, lastModifiedDateTime }) => {
  if (lastModifiedDateTime) {
    return lastModifiedDateTime;
  }
  return createdDateTime;
};

function NoteOwner({ createdBy = null, lastModifiedBy = null }) {
  const theme = useTheme();
  const { firstName, lastName } = getNoteUserName({
    createdBy,
    lastModifiedBy,
  });

  let nameInitials = '';
  if (firstName && lastName) {
    nameInitials = `${firstName?.[0]}${lastName?.[0]}`;
  }
  return (
    <Box display="flex" alignItems="center">
      <Avatar
        sx={{
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.dark,
          width: 30,
          height: 30,
          fontSize: '12px',
          fontWeight: 'bold',
          mr: 1,
        }}
        size="small"
        alt={`${firstName} ${lastName}`}>
        {nameInitials}
      </Avatar>
      <Typography>
        {firstName} {lastName}
      </Typography>
    </Box>
  );
}

function NoteDateTime({ createdDateTime, lastModifiedDateTime }) {
  const dated = getNoteDateTime({ createdDateTime, lastModifiedDateTime });
  return (
    <Typography sx={{ ml: 1 }} variant="caption">
      ({toLongDate(dated)})
    </Typography>
  );
}

function NoteText({ note = '' }) {
  return (
    <Typography
      variant="subtitle1"
      sx={{
        px: 1,
      }}>
      <ReadMoreText maxChars={470}>{note}</ReadMoreText>
    </Typography>
  );
}

function NoteDetails(data = {}) {
  return (
    <Box>
      <Box>
        <NoteText {...data} />
      </Box>
      <Box sx={{ mt: 1 }} display="flex" alignItems="center">
        <Box>
          <NoteOwner {...data} />
        </Box>
        <Box>
          <NoteDateTime {...data} />
        </Box>
      </Box>
    </Box>
  );
}

function NoteButtons({
  data,
  readOnlyRole,
  params,
  initialize,
  setHighlightedRowId,
  noteModalRef,
}) {
  const theme = useTheme();
  const { confirmationRef } = useDialog();
  const { formatMessage } = useLocalization();

  const canUpdate = data?.canUpdate === true;

  const canEdit = !readOnlyRole && canUpdate;

  return (
    <Box textAlign="right" mr={2}>
      {!canEdit && (
        <IconButton
          style={{ background: theme.palette.secondary.light }}
          onClick={() => {
            noteModalRef?.current?.open({
              note: data,
              canEdit: false,
              action: NOTE_ACTION.VIEW,
            });
          }}>
          <RemoveRedEyeIcon style={{ color: theme.palette.primary.main }} />
        </IconButton>
      )}
      {canEdit && (
        <IconButton
          style={{ background: theme.palette.secondary.light }}
          onClick={() => {
            noteModalRef?.current?.open({
              note: data,
              canEdit,
              action: NOTE_ACTION.EDIT,
            });
          }}>
          <Edit style={{ color: theme.palette.primary.main }} />
        </IconButton>
      )}
      {canEdit && (
        <IconButton
          style={{
            background: theme.palette.secondary.light,
            marginLeft: '20px',
          }}
          onClick={() => {
            setHighlightedRowId(data?.id);
            confirmationRef.current.init({
              message: formatMessage({
                id: 'DO_YOU_WANT_TO_DELETE_THIS_NOTE',
                defaultMessage: 'Do you want to delete this note?',
              }),
              positiveButtonText: formatMessage({
                id: 'YES',
                defaultMessage: 'Yes',
              }),
              negativeButtonText: formatMessage({
                id: 'CANCEL',
                defaultMessage: 'Cancel',
              }),
              loadingMessage: formatMessage({
                id: 'DELETING_THE_NOTE',
                defaultMessage: 'Deleting the note...',
              }),
              title: formatMessage({
                id: 'DELETE_NOTE',
                defaultMessage: 'Delete Note',
              }),
              onChange: async value => {
                if (value) {
                  confirmationRef.current.setLoading(true);
                  const result = await deleteNote({
                    params,
                    id: data?.id,
                  });
                  if (result) {
                    params?.snackbar.showMessage(
                      formatMessage({
                        id: 'NOTE_WAS_DELETED_SUCCESSFULLY',
                        defaultMessage: 'Note was deleted successfully',
                      }),
                    );
                    initialize();
                    confirmationRef.current.setLoading(false);
                    setHighlightedRowId(null);
                    confirmationRef.current.setOpen(false);
                  } else {
                    confirmationRef.current.setLoading(false);
                  }
                } else {
                  setHighlightedRowId(null);
                  confirmationRef.current.setLoading(false);
                  confirmationRef.current.setOpen(false);
                }
              },
            });
            confirmationRef.current.setOpen(true);
          }}>
          <Delete style={{ color: theme.palette.error.main }} />
        </IconButton>
      )}
    </Box>
  );
}

const columns = ({
  readOnlyRole,
  params,
  initialize,
  setHighlightedRowId,
  noteModalRef,
}) => {
  return [
    {
      accessorKey: 'detail',
      Cell: props => NoteDetails(props?.row?.original),
      muiTableBodyCellProps: {
        sx: {
          width: '100%',
          display: 'flex',
        },
      },
    },
    {
      accessorKey: 'NoteButtons',
      Cell: props =>
        NoteButtons({
          data: props?.row?.original,
          readOnlyRole,
          params,
          initialize,
          setHighlightedRowId,
          noteModalRef,
        }),
    },
  ];
};

export default columns;
